import React, {useEffect} from "react";
import {matchPath} from "react-router";
import {routes, routesLists} from "../../../../../core/models/static/routes/app-routes";
import {Redirect, Route, Switch, useHistory, useLocation} from "react-router-dom";
import GiftCardViews from "./gift-cards";
import ExportDataView from "./export";
import PriceMatricesManagementView from "../../common/price-matrices";
import PartnershipView from "./partnership";
import SystemSettingsView from "./system-settings";
import ImportDataView from "./import";
import useAccessLevels from "../../../../hooks/use-access-levels";
import PartnerManagementView from "./partner-management";


const SettingsView = () => {
    const location = useLocation();
    const history = useHistory();
    const operatorAccessLevels = useAccessLevels();
    const canAccessSettingsExport = (
        operatorAccessLevels.packages.sales.exportInvoicesList ||
        operatorAccessLevels.packages.base.exportSalesTax ||
        operatorAccessLevels.packages.sales.exportPriceMatrices ||
        operatorAccessLevels.packages.sales.exportTerritoriesAndAccountManagers ||
        operatorAccessLevels.packages.lookup.exportSystemProductsBasicInformation
    );

    /**
     * Listens for the changes in the url and with each change:
     * Checks the url of the route, and if its /configurations, appends content to it.
     */
    useEffect(() => {
        if (matchPath(location.pathname, {path: routes.settings.base, exact: true})) {
            if (operatorAccessLevels.packages.base.organizationSettings) {
                history.replace(routes.settings.organization);
            } else if (operatorAccessLevels.packages.base.personalSettings) {
                history.replace(routes.settings.personal);
            } else if (operatorAccessLevels.packages.sales.viewPriceMatrices) {
                history.replace(routes.settings.priceMatrix.list);
            } else if (operatorAccessLevels.packages.base.partnersManagementSettings) {
                history.replace(routes.settings.partnersManagement);
            } else if (operatorAccessLevels.packages.sales.giftCards) {
                history.replace(routes.settings.giftCards.base);
            } else if (operatorAccessLevels.packages.base.importing) {
                history.replace(routes.settings.import);
            } else if (canAccessSettingsExport) {
                history.replace(routes.settings.export);
            } else if (operatorAccessLevels.packages.base.manageRegions) {
                history.replace(routes.settings.regions);
            } else if (operatorAccessLevels.packages.lookup.manageIndustries) {
                history.replace(routes.settings.industries);
            } else if (operatorAccessLevels.packages.sales.managePriceLevels) {
                history.replace(routes.settings.priceLevels);
            } else if (operatorAccessLevels.packages.base.partnershipSettings) {
                history.replace(routes.settings.partnership);
            } else if (operatorAccessLevels.packages.base.manageTaxes) {
                history.replace(routes.settings.taxes.list);
            } else if (operatorAccessLevels.packages.base.manageTerritories) {
                history.replace(routes.settings.territories);
            } else if (operatorAccessLevels.packages.sales.manageSalesPersonsAccessLevels) {
                history.replace(routes.settings.salesPersons);
            } else if (operatorAccessLevels.packages.lookup.manageProductPropertiesSettings) {
                history.replace(routes.settings.productProperties);
            } else {
                history.replace(routes.settings.base.concat(routes.error.accessDeniedForSection));
            }
            return history.replace(routes.settings.organization);
        }
        if (matchPath(location.pathname, {path: routes.settings.taxes.base, exact: true})) {
            if (operatorAccessLevels.packages.base.manageTaxes) {
                return history.replace(routes.settings.taxes.list);
            } else {
                return history.replace(routes.settings.base.concat(routes.error.accessDeniedForSection));
            }
        }
    }, [canAccessSettingsExport, history, location.pathname, operatorAccessLevels.packages.base.importing, operatorAccessLevels.packages.base.manageRegions, operatorAccessLevels.packages.sales.manageSalesPersonsAccessLevels, operatorAccessLevels.packages.base.manageTaxes, operatorAccessLevels.packages.base.manageTerritories, operatorAccessLevels.packages.base.organizationSettings, operatorAccessLevels.packages.base.partnersManagementSettings, operatorAccessLevels.packages.base.partnershipSettings, operatorAccessLevels.packages.base.personalSettings, operatorAccessLevels.packages.lookup.manageIndustries, operatorAccessLevels.packages.lookup.manageProductPropertiesSettings, operatorAccessLevels.packages.sales.giftCards, operatorAccessLevels.packages.sales.managePriceLevels, operatorAccessLevels.packages.sales.viewPriceMatrices]);

    return (
        <Switch>
            <Route path={routes.settings.base} exact/>
            {
                (
                    operatorAccessLevels.packages.base.organizationSettings ||
                    operatorAccessLevels.packages.base.personalSettings ||
                    operatorAccessLevels.packages.base.manageTaxes ||
                    operatorAccessLevels.packages.base.manageTerritories ||
                    operatorAccessLevels.packages.base.manageRegions ||
                    operatorAccessLevels.packages.lookup.manageIndustries ||
                    operatorAccessLevels.packages.lookup.manageProductPropertiesSettings ||
                    operatorAccessLevels.packages.sales.managePriceLevels ||
                    operatorAccessLevels.packages.sales.manageSalesPersonsAccessLevels
                ) &&
                <Route path={[
                    routes.settings.organization,
                    routes.settings.personal,
                    routes.settings.taxes.list,
                    routes.settings.taxes.regionTaxes,
                    routes.settings.regions,
                    routes.settings.territories,
                    routes.settings.industries,
                    routes.settings.priceLevels,
                    routes.settings.salesPersons,
                    routes.settings.productProperties,
                ]} exact>
                    <SystemSettingsView/>
                </Route>
            }
            {
                operatorAccessLevels.packages.sales.viewPriceMatrices &&
                <Route path={routes.settings.priceMatrix.base}>
                    <PriceMatricesManagementView/>
                </Route>
            }
            {
                operatorAccessLevels.packages.base.partnersManagementSettings &&
                <Route path={routes.settings.partnersManagement}>
                    <PartnerManagementView/>
                </Route>
            }
            <Route path={routesLists.giftCards} exact>
                <GiftCardViews/>
            </Route>
            {
                canAccessSettingsExport &&
                <Route path={routes.settings.export} exact>
                    <ExportDataView/>
                </Route>
            }
            {
                operatorAccessLevels.packages.base.importing &&
                <Route path={routes.settings.import} exact>
                    <ImportDataView/>
                </Route>
            }
            {
                operatorAccessLevels.packages.base.partnershipSettings &&
                <Route path={routes.settings.partnership} exact>
                    <PartnershipView/>
                </Route>
            }
            <Redirect to={routes.settings.base.concat(routes.error.accessDeniedForSection)}/>
        </Switch>
    );
};

export default SettingsView;
